'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date()?.getFullYear());
  }, []);

  const quickLinks = [
    { label: 'Home', path: '/homepage' },
    { label: 'Programs', path: '/programs-overview' },
    { label: 'About Us', path: '/about' },
    { label: 'Success Stories', path: '/success-stories' },
    { label: 'Contact', path: '/contact' }
  ];

  const programs = [
    { label: 'Speed & Athletic Training', path: '/programs-overview' },
    { label: 'Strength & Conditioning', path: '/programs-overview' },
    { label: 'Kids Foundation', path: '/programs-overview' },
    { label: 'General Fitness', path: '/programs-overview' }
  ];

  const socialLinks = [
    { icon: 'facebook', label: 'Facebook', url: 'https://facebook.com' },
    { icon: 'instagram', label: 'Instagram', url: 'https://www.instagram.com/gospeedsprintacademy?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==' },
    { icon: 'twitter', label: 'Twitter', url: 'https://twitter.com' },
    { icon: 'youtube', label: 'YouTube', url: 'https://youtube.com' }
  ];

  return (
    <footer className="w-full bg-primary text-primary-foreground">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-12 lg:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div>
            <Link href="/homepage" className="flex items-center space-x-3 mb-6">
              <div className="relative w-12 h-12 lg:w-16 lg:h-16">
                <Image
                  src="/assets/images/IMG_4331_copy-Photoroom-1770448436115.png"
                  alt="GO Speed Academy Logo"
                  fill
                  className="object-contain"
                />
              </div>
            </Link>
            <p className="text-sm text-white/80 font-body mb-4 leading-relaxed">
              Building character through structured training since 2016. Elite methodology, inclusive approach.
            </p>
            <div className="flex gap-3">
              {socialLinks?.map((social) => (
                <a
                  key={social?.icon}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-accent transition-colors duration-300"
                  aria-label={social?.label}
                >
                  <Icon name="ShareIcon" size={20} className="text-white" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-heading font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks?.map((link) => (
                <li key={link?.path}>
                  <Link
                    href={link?.path}
                    className="text-sm text-white/80 hover:text-accent font-body transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="text-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-heading font-bold mb-4">Our Programs</h3>
            <ul className="space-y-2">
              {programs?.map((program) => (
                <li key={program?.label}>
                  <Link
                    href={program?.path}
                    className="text-sm text-white/80 hover:text-accent font-body transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="text-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {program?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-heading font-bold mb-4">Contact Info</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Icon name="MapPinIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                <p className="text-sm text-white/80 font-body">
                  DPS RKPuram, Vasant Kunj, New Delhi, 110022
                </p>
              </li>
              <li className="flex items-center gap-3">
                <Icon name="PhoneIcon" size={20} className="text-accent flex-shrink-0" />
                <a href="tel:+919871010610" className="text-sm text-white/80 hover:text-accent font-body transition-colors duration-300">
                  +91 98710 10610
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Icon name="EnvelopeIcon" size={20} className="text-accent flex-shrink-0" />
                <a href="mailto:sprintacademygospeed@gmail.com" className="text-sm text-white/80 hover:text-accent font-body transition-colors duration-300">
                  sprintacademygospeed@gmail.com
                </a>
              </li>
            </ul>
            <a
              href="https://forms.gle/9yvnUPvV2ifJin4S9"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-theme-orange text-white font-heading font-bold text-sm rounded-md shadow-hover hover:shadow-modal hover:bg-theme-orange/90 transition-all duration-300"
            >
              <Icon name="CalendarIcon" size={20} />
              Book Trial
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60 font-body text-center md:text-left">
              {isHydrated ? `© ${currentYear}` : '© 2026'} GO Speed Academy. All rights reserved. Building champions since 2016.
            </p>
            <div className="flex gap-6">
              <Link href="/privacy" className="text-sm text-white/60 hover:text-accent font-body transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link href="/terms" className="text-sm text-white/60 hover:text-accent font-body transition-colors duration-300">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;